import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, QrCode, Copy, Download, Trash2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { useShop } from '../../context/ShopContext';
import tableService from '../../services/tableService';
import { generateTableUrl, copyToClipboard, downloadBlob } from '../../utils/urlUtils';
import logger from '../../utils/logger';

// Extracted from legacy Dashboard.js
const TablesTab = () => {
  const { t } = useTranslation();
  const { shop, tables, fetchTables } = useShop();
  const [tableCount, setTableCount] = useState(1);
  const [creatingTables, setCreatingTables] = useState(false);

  const handleCreateTables = async (e) => {
    e.preventDefault();
    if (tableCount <= 0) return;
    setCreatingTables(true);
    try {
      const result = await tableService.createTables(tableCount);
      if (result.success) {
        setTableCount(1);
        fetchTables();
        toast.success(t('dashboard.tablesCreatedSuccessfully'));
      } else {
        toast.error(result.error || t('dashboard.failedToCreateTables'));
      }
    } catch (error) {
      toast.error(t('dashboard.failedToCreateTables'));
    } finally {
      setCreatingTables(false);
    }
  };

  const handleCopyLink = async (table) => {
    const url = generateTableUrl(shop.slug, table.tableNumber);
    const success = await copyToClipboard(url);
    if (success) toast.success(t('dashboard.linkCopiedSuccessfully')); else toast.error(t('dashboard.failedToCopyLink'));
  };

  const handleDownloadQR = async (table) => {
    try {
      const response = await tableService.getTableQRImage(table.id);
      if (response.success) {
        const blob = new Blob([response.data], { type: 'image/png' });
        downloadBlob(blob, `table-${table.tableNumber}-qr.png`);
        toast.success(t('dashboard.qrDownloadedSuccessfully'));
      } else {
        toast.error(response.error || t('dashboard.failedToDownloadQr'));
      }
    } catch {
      toast.error(t('dashboard.failedToDownloadQr'));
    }
  };

  const handleDeleteTable = async (tableId) => {
    if (!window.confirm(t('dashboard.areYouSureDeleteTable'))) return;
    try {
      const result = await tableService.deleteTable(tableId);
      if (result.success) {
        fetchTables();
        toast.success(t('dashboard.tableDeletedSuccessfully'));
      } else {
        toast.error(result.error || t('dashboard.failedToDeleteTable'));
      }
    } catch {
      toast.error(t('dashboard.failedToDeleteTable'));
    }
  };

  useEffect(() => {
    // ensure tables are loaded if not present (context may already do this elsewhere)
    if (!tables) {
      fetchTables();
    }
  }, [tables, fetchTables]);

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-white">{t('dashboard.tablesManagement')}</h2>
        <form onSubmit={handleCreateTables} className="flex items-center space-x-4">
          <input
            type="number"
            min="1"
            max="50"
            value={tableCount}
            onChange={(e) => setTableCount(parseInt(e.target.value) || 1)}
            className="input-field w-24"
          />
          <button type="submit" disabled={creatingTables} className="btn-primary flex items-center space-x-2">
            <Plus className="w-4 h-4" />
            <span>{t('dashboard.generateTables')}</span>
          </button>
        </form>
      </div>
      <div className="mb-6">
        <h3 className="text-xl font-semibold text-white mb-4">{t('dashboard.existingTables')}</h3>
        {tables && tables.length === 0 ? (
          <div className="text-center py-12">
            <QrCode className="w-16 h-16 text-gray-600 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-white mb-2">{t('dashboard.noTablesCreatedYet')}</h3>
            <p className="text-gray-400 mb-4">{t('dashboard.addTablesToGenerateQrCodes')}</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {tables && tables.sort((a,b)=>a.tableNumber-b.tableNumber).map(table => (
              <div key={table.id} className="product-card">
                <div className="text-center mb-4">
                  <h4 className="text-lg font-semibold text-white mb-2">Table {table.tableNumber}</h4>
                  <QRCodeImage tableId={table.id} tableNumber={table.tableNumber} />
                </div>
                <div className="space-y-3">
                  <button onClick={() => handleCopyLink(table)} className="btn-primary w-full">
                    <Copy className="w-4 h-4" />
                    <span>{t('dashboard.copyLink')}</span>
                  </button>
                  <button onClick={() => handleDownloadQR(table)} className="btn-success w-full">
                    <Download className="w-4 h-4" />
                    <span>{t('dashboard.downloadQr')}</span>
                  </button>
                  <button onClick={() => handleDeleteTable(table.id)} className="btn-danger w-full">
                    <Trash2 className="w-4 h-4" />
                    <span>{t('dashboard.deleteTable')}</span>
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const QRCodeImage = ({ tableId, tableNumber }) => {
  const { t } = useTranslation();
  const [imageData, setImageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    const loadQRImage = async () => {
      try {
        setLoading(true);
        setError(false);
        const result = await tableService.getTableQRImage(tableId);
        if (result.success) {
          const blob = new Blob([result.data], { type: 'image/png' });
          const url = URL.createObjectURL(blob);
          setImageData(url);
        } else {
          setError(true);
        }
      } catch (err) {
        logger.error('Failed to load QR image:', err);
        setError(true);
      } finally {
        setLoading(false);
      }
    };
    loadQRImage();
  }, [tableId]);

  useEffect(() => () => { if (imageData) URL.revokeObjectURL(imageData); }, [imageData]);

  return (
    <div className="w-32 h-32 mx-auto mb-4 bg-white rounded-lg p-2 relative">
      {loading && (
        <div className="w-full h-full flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-gray-400 mx-auto mb-2" />
            <p className="text-xs text-gray-500">{t('dashboard.loadingQr')}</p>
          </div>
        </div>
      )}
      {error && (
        <div className="w-full h-full flex items-center justify-center">
          <div className="text-center">
            <QrCode className="w-8 h-8 text-gray-400 mx-auto mb-2" />
            <p className="text-xs text-gray-500">{t('dashboard.qrFailedToLoad')}</p>
          </div>
        </div>
      )}
      {imageData && !loading && !error && (
        <img src={imageData} alt={`QR Code for Table ${tableNumber}`} className="w-full h-full object-contain" />
      )}
    </div>
  );
};

export default TablesTab;
